import { Option } from "@inrev/core";
import { SuretyBond } from "@portal/api/bond/agent";
import type { UseFollowersReturn } from "@portal/api/followers";
import { useFetchAgentPreviews } from "@portal/api/user/agent";
import { FollowerAvatarGroup } from "@portal/components/features/followers/FollowerAvatarGroup";
import { MetricCard } from "@portal/components/patterns/metric-card";
import { SideAnchoredTextBlock } from "@portal/components/patterns/side-anchored-text";
import { Avatar, Icon, Text } from "@portal/components/primitives";
import { ManageFollowersModal } from "@portal/components/ui/FollowerControls";
import { Separator } from "@portal/components/ui/Separator";
import { getStatusTagByBondStatus } from "@portal/components/ui/StatusTag";
import { ReactNode, useState } from "react";
import { Link } from "react-router-dom";
import { carrierLogoSmallMap, suretyBondClosedReasonTypeLabelMap } from "../../../../constants";
import { bondHeaderTermMetrics, formatBondMediumDate } from "../../../shared/bond/bondHeaderTerms";

export type BondCardProps = {
	bond: SuretyBond;
	actionButtons?: ReactNode;
	followersControl: UseFollowersReturn;
};

const lineEffectiveDate = (bond: SuretyBond) =>
	Option.getOrElse(
		(bond.bondPeriods ?? []).find((period) => period.id === bond.currentBondPeriodId)
			?.effectiveDate,
		bond.effectiveDate,
	);

export const BondCard = ({ bond, actionButtons, followersControl }: BondCardProps) => {
	const [manageFollowersOpen, setManageFollowersOpen] = useState(false);
	const underwriter = bond.underwriter;
	const statusTag = getStatusTagByBondStatus(bond.status);
	const dateLine =
		bond.contractSuretyType === "bid"
			? `Bid Date ${formatBondMediumDate(bond.bidDate)}`
			: `Effective ${formatBondMediumDate(lineEffectiveDate(bond))}`;
	const metricItems = bondHeaderTermMetrics(bond);

	return (
		<div
			className="flex w-full flex-col gap-2"
			data-testid="agent-bond-header"
			id="bond_request_card"
		>
			<div className="flex items-center justify-between gap-4">
				{statusTag}
				<div className="flex min-w-0 items-center gap-3">
					<div className="flex min-w-0 items-center gap-2.5">
						{carrierLogoSmallMap[bond.carrierName]}
						<Text type="caption" color="secondary" truncate className="min-w-0 inline-flex">
							{bond.carrierDisplayName}
						</Text>
					</div>
					<Separator orientation="vertical" className="h-4 bg-bc-default" />
					<Text type="caption" color="secondary" className="shrink-0 font-mono inline-flex">
						{bond.number}
					</Text>
				</div>
			</div>
			<div className="flex items-start gap-6">
				<div className="flex min-w-0 flex-1 flex-col gap-2 mb-2">
					<Text type="display" color="primary" className="inline-flex">
						{bond.displayTitle}
					</Text>
					<div className="flex items-baseline gap-x-1">
						<Text type="body" color="secondary">
							for
						</Text>
						<Link to={`/accounts/${bond.accountId}`}>
							<Text type="body" color="secondary" className="hover:underline">
								{bond.principalName}
							</Text>
						</Link>
					</div>
					<div className="flex items-center gap-[6px]">
						<Icon type="calendar" size="mini" color="tertiary" />
						<Text type="body" color="tertiary">
							{dateLine}
						</Text>
					</div>
					{Option.isSome(bond.name) && bond.name.trim() !== "" && (
						<SideAnchoredTextBlock
							text={bond.name}
							testId="agent-bond-project-name"
							trackingId="agent-bond-project-name"
						/>
					)}
					{bond.status === "closed" && Option.isSome(bond.closedAt) && (
						<div className="flex items-center gap-[7px]">
							<Icon type="shield-slash" size="mini" color="secondary" />
							<Text type="body" color="secondary" className="inline-flex">
								Closed {formatBondMediumDate(bond.closedAt)}
								{bond.closedReason
									? ` - ${suretyBondClosedReasonTypeLabelMap[bond.closedReason]}`
									: ""}
							</Text>
						</div>
					)}
				</div>
				<div className="flex shrink-0 flex-col items-end gap-6">
					{actionButtons && <div className="flex w-[148px] flex-col">{actionButtons}</div>}
					<div className="flex flex-col items-end gap-2 xs:flex-row xs:items-center xs:gap-6">
						<div className="flex items-center gap-2">
							<Text type="caption" color="secondary">
								Underwriter
							</Text>
							<Avatar
								data-testid="agent-bond-underwriter"
								className="bg-surface-neutral"
								firstName={underwriter?.firstName}
								lastName={underwriter?.lastName}
								aria-label={
									underwriter?.firstName && underwriter?.lastName
										? `Underwriter ${underwriter.firstName} ${underwriter.lastName}`
										: "Underwriter"
								}
							/>
						</div>
						<div data-testid="agent-bond-header-followers" className="flex items-center gap-2">
							<Text type="caption" color="secondary">
								Agent Followers
							</Text>
							<FollowerAvatarGroup
								followers={followersControl.followers ?? []}
								onAdd={() => setManageFollowersOpen(true)}
								addLabel="Add follower"
							/>
						</div>
					</div>
				</div>
			</div>
			{manageFollowersOpen && (
				<ManageFollowersModal
					{...followersControl}
					manageFollowersSubtext="Followers will receive email notifications for all comments and changes to the status of this bond"
					useFetchAgentPreviews={useFetchAgentPreviews}
					onClose={() => setManageFollowersOpen(false)}
				/>
			)}
			<div className="flex flex-wrap gap-4">
				{metricItems.map(({ label, value }) => (
					<MetricCard key={label} className="min-w-[9rem] flex-1">
						<MetricCard.Label size="md" className="max-w-[9rem]">
							{label}
						</MetricCard.Label>
						<MetricCard.Value size="md" className="mt-auto whitespace-nowrap">
							{value}
						</MetricCard.Value>
					</MetricCard>
				))}
			</div>
		</div>
	);
};
